import React from 'react';
import { ToggleLeft, CornerDownLeft, Calendar } from 'lucide-react';
import { DataTableColumn, FilterOption } from '../../../types/grid';
import { OperatorDef, SearchBarStage, getColumnName, getDatePresets } from './types';
import { Button } from '../../ui/Button';
import { Input } from '../../ui/Input';

interface FilterSearchValueMenuProps<T extends Record<string, any>> {
  stage: SearchBarStage;
  pendingColumn: DataTableColumn<T>;
  pendingOperator: OperatorDef | null;
  inputValue: string;
  highlightedIndex: number;
  matchingBooleanOptions: Array<{ label: string; value: string }>;
  matchingEnumOptions: FilterOption[];
  pendingOptions: FilterOption[];
  matchingOptions: FilterOption[];
  onCommitBoolean: (val: string) => void;
  onCommitEnum: (val: string) => void;
  onCommitValue: (val: string) => void;
  onBackToColumnStage: () => void;
  onBackToOperatorStage: () => void;
  onHoverIndex: (idx: number) => void;
}

export function FilterSearchValueMenu<T extends Record<string, any>>({
  stage,
  pendingColumn,
  pendingOperator,
  inputValue,
  highlightedIndex,
  matchingBooleanOptions,
  matchingEnumOptions,
  pendingOptions,
  matchingOptions,
  onCommitBoolean,
  onCommitEnum,
  onCommitValue,
  onBackToColumnStage,
  onBackToOperatorStage,
  onHoverIndex,
}: FilterSearchValueMenuProps<T>) {
  if (stage === 'boolean') {
    return (
      <div>
        <div className="px-2.5 py-1 text-xs font-semibold text-cf-textMuted uppercase tracking-wider flex items-center justify-between border-b border-cf-border pb-1 mb-1">
          <span>{getColumnName(pendingColumn)}</span>
          <Button
            type="button"
            variant="link"
            size="xs"
            onClick={onBackToColumnStage}
            className="text-cf-accent normal-case font-normal p-0 h-auto"
          >
            Back
          </Button>
        </div>
        <div className="space-y-0.5 py-0.5">
          {matchingBooleanOptions.map((opt, idx) => {
            const isSelected = highlightedIndex === idx;
            return (
              <Button
                key={opt.value}
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => onCommitBoolean(opt.value)}
                onMouseEnter={() => onHoverIndex(idx)}
                className={`w-full flex items-center justify-between px-3 py-1.5 rounded text-left text-xs transition-colors ${
                  isSelected
                    ? 'bg-zinc-100 dark:bg-zinc-700/80 ring-1 ring-blue-500/50 text-cf-textPrimary font-medium'
                    : 'text-cf-textSecondary hover:bg-cf-surfaceHover hover:text-cf-textPrimary'
                }`}
              >
                <div className="flex items-center gap-2">
                  <ToggleLeft size={13} className="text-purple-500" />
                  <span className="font-medium">{opt.label}</span>
                </div>
                <CornerDownLeft size={11} className="text-cf-textMuted" />
              </Button>
            );
          })}
        </div>
      </div>
    );
  }

  if (stage === 'enum') {
    return (
      <div>
        <div className="px-2.5 py-1 text-xs font-semibold text-cf-textMuted uppercase tracking-wider flex items-center justify-between border-b border-cf-border pb-1 mb-1">
          <span>{getColumnName(pendingColumn)}</span>
          <Button
            type="button"
            variant="link"
            size="xs"
            onClick={onBackToColumnStage}
            className="text-cf-accent normal-case font-normal p-0 h-auto"
          >
            Back
          </Button>
        </div>
        <div className="max-h-56 overflow-y-auto space-y-0.5 py-0.5">
          {matchingEnumOptions.length === 0 && (
            <div className="px-3 py-2 text-xs text-cf-textMuted italic">
              No matching values found
            </div>
          )}
          {matchingEnumOptions.map((opt, idx) => {
            const isSelected = highlightedIndex === idx;
            return (
              <Button
                key={opt.value}
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => onCommitEnum(opt.value)}
                onMouseEnter={() => onHoverIndex(idx)}
                className={`w-full flex items-center justify-between px-3 py-1.5 rounded text-left text-xs transition-colors ${
                  isSelected
                    ? 'bg-zinc-100 dark:bg-zinc-700/80 ring-1 ring-blue-500/50 text-cf-textPrimary font-medium'
                    : 'text-cf-textSecondary hover:bg-cf-surfaceHover hover:text-cf-textPrimary'
                }`}
              >
                <div className="flex items-center gap-2">
                  <span
                    className={`w-2 h-2 rounded-full ${
                      opt.color === 'emerald'
                        ? 'bg-emerald-500'
                        : opt.color === 'rose'
                        ? 'bg-rose-500'
                        : opt.color === 'amber'
                        ? 'bg-amber-500'
                        : 'bg-cf-orange'
                    }`}
                  />
                  <span className="font-medium">{opt.label}</span>
                </div>
                <CornerDownLeft size={11} className="text-cf-textMuted" />
              </Button>
            );
          })}
        </div>
      </div>
    );
  }

  if (stage === 'value' && pendingOperator) {
    return (
      <div>
        <div className="px-2.5 py-1 text-xs font-semibold text-cf-textMuted uppercase tracking-wider flex items-center justify-between border-b border-cf-border pb-1 mb-1">
          <span>
            {getColumnName(pendingColumn)} {pendingOperator.symbol}
          </span>
          <Button
            type="button"
            variant="link"
            size="xs"
            onClick={onBackToOperatorStage}
            className="text-cf-accent normal-case font-normal p-0 h-auto"
          >
            Back
          </Button>
        </div>

        {pendingOptions.length > 0 ? (
          <div className="max-h-56 overflow-y-auto space-y-0.5 py-0.5">
            {matchingOptions.map((opt, idx) => {
              const isSelected = highlightedIndex === idx;
              return (
                <Button
                  key={opt.value}
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => onCommitValue(opt.value)}
                  onMouseEnter={() => onHoverIndex(idx)}
                  className={`w-full flex items-center justify-between px-2.5 py-1.5 rounded text-left text-xs transition-colors ${
                    isSelected
                      ? 'bg-cf-surfaceHover text-cf-textPrimary font-medium'
                      : 'text-cf-textSecondary hover:bg-cf-surfaceHover hover:text-cf-textPrimary'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <span
                      className={`w-2 h-2 rounded-full ${
                        opt.color === 'emerald'
                          ? 'bg-emerald-500'
                          : opt.color === 'rose'
                          ? 'bg-rose-500'
                          : opt.color === 'amber'
                          ? 'bg-amber-500'
                          : 'bg-cf-orange'
                      }`}
                    />
                    <span>{opt.label}</span>
                  </div>
                  <CornerDownLeft size={11} className="text-cf-textMuted" />
                </Button>
              );
            })}
          </div>
        ) : pendingColumn.dataType === 'date' ? (
          <div className="p-2 space-y-2.5">
            <div className="flex items-center justify-between text-xs text-cf-textMuted border-b border-cf-border pb-1.5">
              <span className="font-medium text-cf-textSecondary">Select or type date</span>
              <span className="font-sans bg-cf-surfaceHover px-1.5 py-0.5 rounded text-xs text-cf-textMuted border border-cf-border">
                YYYY-MM-DD
              </span>
            </div>

            {/* Native Date Picker trigger */}
            <div className="flex items-center gap-2 px-2.5 py-1.5 bg-cf-surface border border-cf-border hover:border-zinc-500 rounded focus-within:border-cf-accent focus-within:ring-1 focus-within:ring-cf-accent/30 transition-all">
              <Calendar size={13} className="text-sky-500 shrink-0 pointer-events-none" />
              <Input
                type="date"
                onChange={(e) => {
                  if (e.target.value) {
                    onCommitValue(e.target.value);
                  }
                }}
                className="w-full !bg-transparent bg-transparent text-xs text-cf-textPrimary outline-none cursor-pointer scheme-dark font-sans !p-0 !border-none"
                style={{ backgroundColor: 'transparent' }}
              />
            </div>

            {/* Quick Presets */}
            <div>
              <div className="text-xs font-semibold text-cf-textMuted uppercase tracking-wider mb-1.5">
                Quick Presets
              </div>
              <div className="grid grid-cols-2 gap-1">
                {getDatePresets().map((preset) => (
                  <Button
                    key={preset.label}
                    type="button"
                    variant="ghost"
                    size="xs"
                    onClick={() => onCommitValue(preset.value)}
                    className="flex items-center justify-between px-2 py-1 rounded bg-cf-surfaceHover/60 hover:bg-cf-surfaceHover hover:text-cf-textPrimary text-cf-textSecondary text-xs border border-cf-border/60 transition-colors text-left"
                  >
                    <span className="font-medium">{preset.label}</span>
                    <span className="font-sans text-xs text-cf-textMuted">{preset.value}</span>
                  </Button>
                ))}
              </div>
            </div>

            {/* Apply button if text is typed */}
            {inputValue.trim() && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => onCommitValue(inputValue)}
                className="w-full flex items-center justify-between px-2.5 py-1.5 rounded bg-blue-500/10 text-blue-600 dark:text-blue-400 font-medium text-xs hover:bg-blue-500/20 transition-colors"
              >
                <span>
                  Apply date: <span className="font-bold text-cf-textPrimary">"{inputValue}"</span>
                </span>
                <CornerDownLeft size={11} />
              </Button>
            )}
          </div>
        ) : (
          <div className="p-2 space-y-2">
            <div className="text-xs text-cf-textSecondary">
              Type a value and press <kbd className="px-1.5 py-0.5 bg-cf-surfaceHover border border-cf-border rounded text-xs font-mono">Enter</kbd>
            </div>
            {inputValue.trim() && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => onCommitValue(inputValue)}
                className="w-full flex items-center justify-between px-2.5 py-1.5 rounded bg-blue-500/10 text-blue-600 dark:text-blue-400 font-medium text-xs hover:bg-blue-500/20 transition-colors"
              >
                <span>
                  Apply: <span className="font-bold text-cf-textPrimary">"{inputValue}"</span>
                </span>
                <CornerDownLeft size={11} />
              </Button>
            )}
          </div>
        )}
      </div>
    );
  }

  return null;
}
